import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  // NEW: keep free products clean (no duplicates) on any cart change
  #debouncedSyncFreeProducts = debounce(this.#syncFreeProducts, 500).bind(this);
  #isSyncingFreeProducts = false;
  #lastCartFetchTime = 0;
  #minCartFetchInterval = 1000; // Minimum 1 second between cart.js requests

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Some themes dispatch cartAdd separately; syncing here prevents duplicates right after add
    document.addEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);

    // Ensure free products are at quantity 1 on load
    this.#checkFreeProductsQuantity();

    // NEW: dedupe free products on load
    this.#debouncedSyncFreeProducts();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(ThemeEvents.cartAdd, this.#handleCartAdd);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    // Prevent quantity changes for free products (except removal - quantity 0)
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (lineItemRow?.dataset.freeProduct === 'true' && quantity > 0) {
      // Reset quantity to 1 for free products
      const quantitySelector = this.refs.quantitySelectors[line - 1];
      const quantityInput = quantitySelector?.querySelector('input');
      if (quantityInput) {
        quantityInput.value = '1';
      }
      return;
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  async onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();
    if (prefersReducedMotion()) return remove();

    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;

    // Prevent quantity changes for free products (except removal - quantity 0)
    const lineItemRow = this.refs.cartItemRows[line - 1];
    if (lineItemRow?.dataset.freeProduct === 'true' && quantity > 0) {
      this.#enableCartItems();
      const quantitySelector = this.refs.quantitySelectors[line - 1];
      const quantityInput = quantitySelector?.querySelector('input');
      if (quantityInput) {
        quantityInput.value = '1';
      }
      return;
    }

    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        // NEW: after any change, normalize free products (remove duplicates / enforce qty 1)
        this.#debouncedSyncFreeProducts();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles cartAdd (some themes fire add separately from update).
   * @param {CartAddEvent} _event
   */
  #handleCartAdd = (_event) => {
    this.#debouncedSyncFreeProducts();
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      // NEW: after rerender, normalize free products
      this.#debouncedSyncFreeProducts();
      return;
    }

    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    // NEW: normalize free products after any external cart update
    this.#debouncedSyncFreeProducts();
  };

  /**
   * Checks and ensures free products are at quantity 1 on component load.
   */
  #checkFreeProductsQuantity = () => {
    setTimeout(() => {
      const freeProductRows = this.querySelectorAll('[data-free-product="true"]');
      freeProductRows.forEach((row) => {
        const quantitySelector = row.querySelector('quantity-selector');
        if (quantitySelector) {
          const input = quantitySelector.querySelector('input');
          if (input && input.value !== '1') {
            input.value = '1';
          }
        }
      });
    }, 100);
  };

  /**
   * NEW: Determine if an item is a "Back order" item by checking its properties keys.
   * @param {Record<string, any> | null | undefined} props
   */
  #hasBackOrderProperty(props) {
    if (!props) return false;
    return Object.keys(props).some((key) => {
      const normalized = key.toLowerCase().replace(/[\s_]/g, '');
      return normalized.includes('back') && normalized.includes('order');
    });
  }

  /**
   * NEW: Determine if an item is a free product by checking its properties keys.
   * @param {Record<string, any> | null | undefined} props
   */
  #isFreeProduct(props) {
    if (!props) return false;

    // Most common formats:
    if (props._Free_Product !== undefined) return true;
    if (props['_Free Product'] !== undefined) return true;
    if (props['_Free_Product'] !== undefined) return true;

    // Fallback: key contains "free product"
    return Object.keys(props).some((key) => {
      const normalized = key.toLowerCase().replace(/[\s_]/g, '');
      return normalized.includes('free') && normalized.includes('product');
    });
  }

  /**
   * NEW: Keep free products ONLY once in cart.
   * - If cart has at least one Back order item:
   *   - keep 1 line per free-product variant, force qty = 1, remove duplicates
   * - If no Back order items:
   *   - remove all free products
   */
  async #syncFreeProducts() {
    if (this.#isSyncingFreeProducts) return;
    
    // Rate limiting: prevent too frequent requests
    const now = Date.now();
    if (now - this.#lastCartFetchTime < this.#minCartFetchInterval) {
      return;
    }
    
    this.#isSyncingFreeProducts = true;

    try {
      this.#lastCartFetchTime = now;
      const cartResponse = await fetch(Theme.routes.root + 'cart.js');
      
      // Handle rate limiting (429 errors)
      if (cartResponse.status === 429) {
        console.warn('[Cart] Rate limited, skipping free products sync');
        return;
      }
      
      if (!cartResponse.ok) {
        throw new Error(`Cart fetch failed: ${cartResponse.status}`);
      }
      
      const cartData = await cartResponse.json();

      const items = Array.isArray(cartData.items) ? cartData.items : [];
      if (items.length === 0) return;

      const hasBackOrder = items.some((item) => this.#hasBackOrderProperty(item.properties));
      const freeItems = items.filter((item) => this.#isFreeProduct(item.properties));

      if (freeItems.length === 0) return;

      /** @type {{ key: string; quantity: number }[]} */
      const changes = [];

      if (!hasBackOrder) {
        // No backorder items => free products should not remain in cart
        freeItems.forEach((item) => {
          if (item?.key) changes.push({ key: item.key, quantity: 0 });
        });
      } else {
        // Backorder exists => only 1 of each free-product variant allowed (qty forced to 1)
        const byVariant = new Map();

        freeItems.forEach((item) => {
          const variantId = String(item.variant_id);
          if (!byVariant.has(variantId)) byVariant.set(variantId, []);
          byVariant.get(variantId).push(item);
        });

        for (const [, group] of byVariant.entries()) {
          // Keep first, remove rest
          const keeper = group[0];
          if (keeper?.key && keeper.quantity !== 1) {
            changes.push({ key: keeper.key, quantity: 1 });
          }

          for (let i = 1; i < group.length; i++) {
            const extra = group[i];
            if (extra?.key) changes.push({ key: extra.key, quantity: 0 });
          }
        }
      }

      if (changes.length === 0) return;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      let lastParsed = null;

      // Use `id` (line item key) so we don't care about line shifting
      for (const change of changes) {
        const body = JSON.stringify({
          id: change.key,
          quantity: change.quantity,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname,
        });

        const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
        const responseText = await response.text();
        lastParsed = JSON.parse(responseText);

        if (lastParsed?.errors) {
          console.error('Free product sync error:', lastParsed.errors);
          return;
        }
      }

      if (lastParsed?.sections) {
        Object.keys(lastParsed.sections).forEach((sectionId) => {
          morphSection(sectionId, lastParsed.sections[sectionId]);
        });

        // Dispatch a single cart update for other listeners (cart bubble, etc.)
        // Rate limiting: wait if needed
        const waitTime = this.#minCartFetchInterval - (Date.now() - this.#lastCartFetchTime);
        if (waitTime > 0) {
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
        
        this.#lastCartFetchTime = Date.now();
        const newCartResponse = await fetch(Theme.routes.root + 'cart.js');
        
        if (newCartResponse.status === 429) {
          console.warn('[Cart] Rate limited on cart update fetch');
          return;
        }
        
        if (!newCartResponse.ok) {
          throw new Error(`Cart update fetch failed: ${newCartResponse.status}`);
        }
        
        const newCartData = await newCartResponse.json();

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartData.item_count,
            source: 'cart-items-component:free-products-sync',
            sections: lastParsed.sections,
          })
        );

        // Enforce visible qty = 1 in UI too
        this.#checkFreeProductsQuantity();
      }
    } catch (error) {
      console.error('Error syncing free products:', error);
    } finally {
      this.#isSyncingFreeProducts = false;
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
